import { useEffect, useState } from 'react';

import { Button } from '@/marketing/components/ui/button';
import { Input } from '@/marketing/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/marketing/components/ui/dialog';
import type { CommissionTierTrio, InfluencerVoucher } from '@/marketing/models/voucher';

/** Full replacement of the code's overrides: an omitted rate clears it so the code inherits again. */
export interface CommissionRatesSubmit {
  commissionPercent?: number;
  utmPercent?: number;
  couponUtmPercent?: number;
}

interface VoucherCommissionDialogProps {
  voucher: InfluencerVoucher | null;
  tierDefaults: CommissionTierTrio | null;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (rates: CommissionRatesSubmit) => void;
}

const sanitizePercent = (raw: string): string => raw.replace(/[^0-9.]/g, '');

const isValidPercent = (value: string): boolean => {
  const parsed = Number(value);
  return value.length > 0 && Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;
};

interface OverrideFieldProps {
  label: string;
  value: string;
  defaultPercent: number | undefined;
  onChange: (value: string) => void;
}

// Link-tier field: blank means the code inherits the program default shown in the placeholder.
function OverrideField({ label, value, defaultPercent, onChange }: OverrideFieldProps) {
  return (
    <label className="tw-flex tw-flex-col tw-gap-1">
      <span className="tw-flex tw-items-baseline tw-justify-between">
        <span className="tw-text-xs tw-font-semibold">{label}</span>
        {value !== '' && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="tw-text-[11px] tw-text-muted-foreground hover:tw-text-foreground"
          >
            Use default
          </button>
        )}
      </span>
      <span className="tw-relative">
        <Input
          value={value}
          onChange={(event) => onChange(sanitizePercent(event.target.value))}
          inputMode="decimal"
          placeholder={defaultPercent === undefined ? '' : `${defaultPercent} · default`}
          className="tw-pr-7"
        />
        <span className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-sm tw-text-muted-foreground">
          %
        </span>
      </span>
    </label>
  );
}

export function VoucherCommissionDialog({
  voucher,
  tierDefaults,
  isPending,
  onClose,
  onSubmit,
}: VoucherCommissionDialogProps) {
  const [coupon, setCoupon] = useState('');
  const [utm, setUtm] = useState('');
  const [couponUtm, setCouponUtm] = useState('');

  useEffect(() => {
    setCoupon(voucher?.commissionPercent !== undefined ? String(voucher.commissionPercent) : '');
    setUtm(voucher?.utmPercent !== undefined ? String(voucher.utmPercent) : '');
    setCouponUtm(voucher?.couponUtmPercent !== undefined ? String(voucher.couponUtmPercent) : '');
  }, [voucher]);

  const isCouponValid = coupon === '' || isValidPercent(coupon);
  const isUtmValid = utm === '' || isValidPercent(utm);
  const isCouponUtmValid = couponUtm === '' || isValidPercent(couponUtm);

  const draftOf = (percent: number | undefined): string => (percent !== undefined ? String(percent) : '');
  const hasChanges =
    voucher !== null &&
    (coupon !== draftOf(voucher.commissionPercent) ||
      utm !== draftOf(voucher.utmPercent) ||
      couponUtm !== draftOf(voucher.couponUtmPercent));

  const canSubmit = isCouponValid && isUtmValid && isCouponUtmValid && hasChanges && !isPending;

  const submit = () => {
    onSubmit({
      ...(coupon !== '' ? { commissionPercent: Number(coupon) } : {}),
      ...(utm !== '' ? { utmPercent: Number(utm) } : {}),
      ...(couponUtm !== '' ? { couponUtmPercent: Number(couponUtm) } : {}),
    });
  };

  return (
    <Dialog open={voucher !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="tw-max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Commission rates — {voucher?.code}</DialogTitle>
          <DialogDescription>
            One rate per tracking source. Leave a field blank to inherit the program default.
            Applies from now on — already attributed orders keep the rate they were recorded at,
            and the audience discount does not change.
          </DialogDescription>
        </DialogHeader>

        <div className="tw-flex tw-flex-col tw-gap-3">
          <OverrideField
            label="Coupon only"
            value={coupon}
            defaultPercent={tierDefaults?.couponPercent}
            onChange={setCoupon}
          />
          <OverrideField
            label="Link only"
            value={utm}
            defaultPercent={tierDefaults?.utmPercent}
            onChange={setUtm}
          />
          <OverrideField
            label="Link + coupon"
            value={couponUtm}
            defaultPercent={tierDefaults?.couponUtmPercent}
            onChange={setCouponUtm}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {isPending ? 'Saving…' : 'Save rates'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
